import React, { memo, useCallback, useEffect, useState } from "react";
import AddCategory from "../addCategory/AddCategory";
import baseUrl from "../../../../../api/baseUrl";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CategoryIcon from "@mui/icons-material/Category";
import SearchIcon from "@mui/icons-material/Search";

interface Category {
  _id: string;
  categoryName: string;
}

const AllCategory: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<Category[]>([]);
  const [id, setId] = useState<string>("");
  const [search, setSearch] = useState("");

  const getData = useCallback(() => {
    baseUrl
      .get("client/inventory/category/findAllCategory")
      .then((response) => setData(response.data.data))
      .catch((error) => console.error(error?.response));
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const closeModal = useCallback(() => {
    setOpen(false);
    setId("");
    getData();
  }, [getData]);

  const openModal = useCallback(() => setOpen(true), []);

  const update = useCallback((id: string) => {
    setId(id);
    setOpen(true);
  }, []);

  const filteredData = data.filter((item) =>
    item.categoryName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Header Card */}
      <div className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3 mb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
          <div className="relative flex-1 max-w-md">
            <SearchIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              sx={{ fontSize: 16 }}
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search categories..."
              className="w-full pl-10 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs"
            />
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-lg">
              <CategoryIcon className="text-emerald-500" sx={{ fontSize: 16 }} />
              <span className="font-bold text-xs text-emerald-600">{data.length}</span>
              <span className="text-slate-500 text-[10px]">Categories</span>
            </div>
            <button
              onClick={openModal}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Add Category
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Mobile Cards */}
        <div className="md:hidden p-3 space-y-2">
          {filteredData.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <CategoryIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No categories found</p>
            </div>
          ) : (
            filteredData.map((item, i) => (
              <div key={item._id} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-primaryLight rounded-full flex items-center justify-center text-white font-semibold text-xs">
                      {i + 1}
                    </div>
                    <span className="font-semibold text-xs text-slate-800">{item.categoryName}</span>
                  </div>
                  <button
                    onClick={() => update(item._id)}
                    className="p-1.5 bg-emerald-50 text-emerald-600 rounded-md hover:bg-emerald-100 transition-colors"
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">No</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Category Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-8 text-center text-slate-400">
                    <CategoryIcon sx={{ fontSize: 36 }} className="mb-2" />
                    <p className="text-xs">No categories found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item, i) => (
                  <tr key={item._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-slate-100 text-slate-600">
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 bg-gradient-to-br from-primary to-primaryLight rounded-full flex items-center justify-center text-white text-[10px] font-semibold">
                          {item.categoryName.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-xs text-slate-800">{item.categoryName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => update(item._id)}
                        className="p-1.5 rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                        title="Edit"
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && <AddCategory id={id} closeModal={closeModal} />}
    </>
  );
};

export default AllCategory;
